import { TraitId } from '@cibo/core'
import {
  CropManagementDataResourceDetailFeature,
  DairySoilsEnrollment,
  EarlyAdopterTillageFeature,
  EnrolledOtherCarbon,
  PLUIdDetailFeature,
  PreferredRankPracticeFeature,
  ProductAppliedPerPlan,
  RDCottonNitrogenReductionResourceDetailFeature,
  RDDescriptionFeature,
  RDEqipEligibilityConservationACEPResourceDetailFeature,
  RDEqipEligibilityConservationCRPResourceDetailFeature,
  RDEqipEligibilityConservationCSPResourceDetailFeature,
  RDEqipEligibilityFarmInfoDetailFeature,
  RDEqipEligibilityLandControlResourceDetailFeature,
  RDEqipEligibilityWetlandComplianceResourceDetailFeature,
  RDEqipPracticesFloodingResourceDetailFeature,
  RDEqipPracticesIrrigationMethodResourceDetailFeature,
  RDEqipPracticesIrrigationQuantityResourceDetailFeature,
  RDEqipPracticesNaturalMoistureResourceDetailFeature,
  RDEqipPracticesSeepsResourceDetailFeature,
  RDEqipPracticesSnowDriftsResourceDetailFeature,
  RDEqipPracticesSoilCompactionResourceDetailFeature,
  RDEqipPracticesSoilCoverDetailFeature,
  RDEqipPracticesSoilErosionClassicalGullyResourceDetailFeature,
  RDEqipPracticesSoilErosionEphemeralGullyResourceDetailFeature,
  RDEqipPracticesSoilSalinityResourceDetailFeature,
  RDEqipPracticesSoilSubsidenceResourceDetailFeature,
  RDEqipPracticesWaterTableDetailFeature,
  RDOnFarmDairyFeedFeature,
  RDUsdaFederalProgramEnrollmentResourceDetailFeature,
} from '@cibo/landmanager'
import {
  EnrolledInUSCottonTrust,
  RDLolFarmIdResourceDetailFeature,
  UsdaEarlyAdopter,
} from '@cibo/profile'
import { ResourceDetailFeature } from '@cibo/ui'
import {
  EqipContractResultsFeatureMap,
  EqipFarmInformationDetailFeatureMap,
  RDEqipAutogenRequiredFormsFeatureMap,
  RDEqipCurrentPracticesDetailFeature,
  RDEqipDocumentsFeatureMap,
  RDEqipFieldsSelectionFeature,
  RDEqipFsaFormsResourceDetailFeature,
  RDEqipHasFarmInfoResourceDetailFeature,
  RDEqipOptionalFormsResourceDetailFeature,
  RDEqipPracticeChoiceAgroforestryResourceDetailFeature,
  RDEqipPracticeChoiceEngineeringLandErosionStructuresResourceDetailFeature,
  RDEqipPracticeChoiceEngineeringWaterControlStructuresResourceDetailFeature,
  RDEqipPracticeChoiceSoilResourceDetailFeature,
  RDEqipPracticeChoiceWildlifeResourceDetailFeature,
  RDEqipPracticeSelectionConfirmationFeature,
  RDEqipProofOfLandControlFeatureMap,
  RDEqipRecommendedPracticesIntentResourceDetailFeature,
  RDEqipRequiredFormsResourceDetailFeature,
  RDEqipReviewRecommendationResultsResourceDetailFeature,
  TraitTableTaskEditor,
} from '../../../../features/ResourceDetailFeatures'
import { RDEqipApplicationResultsResourceDetailFeature } from '../../../../features/ResourceDetailFeatures/EQIP/RDEqipApplicationResultsResourceDetailFeature'
import { ConservationProgramsListTaskDefinition } from '../../../ResourceDetailFeatures/ConservationProgramsList/ConservationProgramsListTaskDefinition'
import { AdvisorInformationFeatureMap } from '../../../ResourceDetailFeatures/EQIP/AdvisorInformation'
import { EntityContactInformationFeatureMap } from '../../../ResourceDetailFeatures/EQIP/EntityContactInformation'
import { EntityMembersFeatureMap } from '../../../ResourceDetailFeatures/EQIP/EntityMembers'
import { EqipFarmNumbersFeatureMap } from '../../../ResourceDetailFeatures/EQIP/EqipFarmNumbers'
import { RDUsdaAgiThresholdResourceDetailFeature } from '../../../ResourceDetailFeatures/EQIP/UsdaAgiThreshold'
import { UsdaGrowerDetailsFeatureMap } from '../../../ResourceDetailFeatures/EQIP/UsdaGrowerDetails'
import {
  RDUsdaGrowerTypeResourceDetailFeature,
  RDUsdaGrowerTypeYearlyResourceDetailFeature,
} from '../../../ResourceDetailFeatures/EQIP/UsdaGrowerType'
import { RDUsdaUnderservedSmallResourceDetailFeature } from '../../../ResourceDetailFeatures/EQIP/UsdaUnderservedSmall'
import { GovernmentFundedTaskDefinition } from '../../../ResourceDetailFeatures/GovernmentFunded'

const LegacyTraitTableFeatures = [
  'agronomicDataCsv',
  'amendment',
  'amendment',
  'amendmentSummary',
  'biological',
  'biomassBurning',
  'cashCrop',
  'cornNitrogenApplication',
  'coverCrop',
  'eventIrrigation',
  'farmManagementSystem',
  'fertilizer',
  'fertilizerReduction',
  'futureCashCrop',
  'futureCoverCrop',
  'futureLivestockChange',
  'futureTillage',
  'grazing',
  'includesWetlandDesignation',
  'irrigation',
  'liming',
  'livestock',
  'nativeVegetationConversion',
  'nativeVegetationConversionDate',
  'naturalDisturbance',
  'ownership',
  'shipmentDistance',
  'tillage',
  'waterPumpIrrigation',
].reduce(
  (acc, traitId) => ({
    ...acc,
    [traitId]: {
      Component: {
        traitId,
        TaskEditor: TraitTableTaskEditor,
      },
      resourceType: 'field',
    },
  }),
  {}
)

export const ResourceDetailFeatureMap: Partial<
  Record<
    TraitId,
    {
      Component: ResourceDetailFeature<any>
      resourceType: 'field' | 'user' | 'workflow'
    }
  >
> = {
  advisorInformation: {
    Component: AdvisorInformationFeatureMap as ResourceDetailFeature<any>,
    resourceType: 'workflow',
  },
  cottonNitrogenReduction: {
    Component: RDCottonNitrogenReductionResourceDetailFeature,
    resourceType: 'field',
  },
  /** @deprecated */
  dairySoilsEnrollment: {
    Component: DairySoilsEnrollment,
    resourceType: 'field',
  },
  description: {
    Component: RDDescriptionFeature,
    resourceType: 'field',
  },
  earlyAdopterTillage: {
    Component: EarlyAdopterTillageFeature,
    resourceType: 'field',
  },
  enrolledOtherCarbon: {
    Component: EnrolledOtherCarbon,
    resourceType: 'field',
  },
  enrolledAnyCarbon: {
    Component: EnrolledOtherCarbon,
    resourceType: 'field',
  },
  cropManagementData: {
    Component: CropManagementDataResourceDetailFeature,
    resourceType: 'field',
  },
  eqipApplicationResults: {
    Component: RDEqipApplicationResultsResourceDetailFeature,
    resourceType: 'workflow',
  },
  eqipContractResults: EqipContractResultsFeatureMap,
  eqipPLUId: {
    Component: PLUIdDetailFeature,
    resourceType: 'field',
  },
  eqipCurrentPractices: {
    Component: RDEqipCurrentPracticesDetailFeature,
    resourceType: 'field',
  },
  eqipEligibilityFarmInfo: {
    Component: RDEqipEligibilityFarmInfoDetailFeature,
    resourceType: 'field',
  },
  eqipEligibilityLandControl: {
    Component: RDEqipEligibilityLandControlResourceDetailFeature,
    resourceType: 'field',
  },
  eqipEligibilityConservationACEP: {
    Component: RDEqipEligibilityConservationACEPResourceDetailFeature,
    resourceType: 'field',
  },
  eqipEligibilityConservationCRP: {
    Component: RDEqipEligibilityConservationCRPResourceDetailFeature,
    resourceType: 'field',
  },
  eqipEligibilityConservationCSP: {
    Component: RDEqipEligibilityConservationCSPResourceDetailFeature,
    resourceType: 'field',
  },
  eqipEligibilityWetlandCompliance: {
    Component: RDEqipEligibilityWetlandComplianceResourceDetailFeature,
    resourceType: 'field',
  },
  eqipEntity: {
    Component: EntityContactInformationFeatureMap as ResourceDetailFeature<any>,
    resourceType: 'workflow',
  },

  eqipFarmNumbers: EqipFarmNumbersFeatureMap as {
    Component: ResourceDetailFeature<any>
    resourceType: 'workflow'
  },

  eqipPracticesIrrigationMethod: {
    Component: RDEqipPracticesIrrigationMethodResourceDetailFeature,
    resourceType: 'field',
  },
  eqipPracticesSeeps: {
    Component: RDEqipPracticesSeepsResourceDetailFeature,
    resourceType: 'field',
  },
  eqipPracticesSnowDrifts: {
    Component: RDEqipPracticesSnowDriftsResourceDetailFeature,
    resourceType: 'field',
  },
  eqipPracticesSoilCompaction: {
    Component: RDEqipPracticesSoilCompactionResourceDetailFeature,
    resourceType: 'field',
  },
  eqipPracticesSoilCover: {
    Component: RDEqipPracticesSoilCoverDetailFeature,
    resourceType: 'field',
  },
  eqipPracticesSoilErosionClassicalGully: {
    Component: RDEqipPracticesSoilErosionClassicalGullyResourceDetailFeature,
    resourceType: 'field',
  },
  eqipPracticesSoilErosionEphemeralGully: {
    Component: RDEqipPracticesSoilErosionEphemeralGullyResourceDetailFeature,
    resourceType: 'field',
  },
  eqipPracticesSoilSubsidence: {
    Component: RDEqipPracticesSoilSubsidenceResourceDetailFeature,
    resourceType: 'field',
  },
  eqipPracticesSoilSalinity: {
    Component: RDEqipPracticesSoilSalinityResourceDetailFeature,
    resourceType: 'field',
  },
  eqipPracticesFlooding: {
    Component: RDEqipPracticesFloodingResourceDetailFeature,
    resourceType: 'field',
  },
  eqipPracticesWaterTable: {
    Component: RDEqipPracticesWaterTableDetailFeature,
    resourceType: 'field',
  },
  eqipFarmInformation: EqipFarmInformationDetailFeatureMap as {
    Component: ResourceDetailFeature<any>
    resourceType: 'workflow'
  },
  eqipHasFarmInfo: {
    Component: RDEqipHasFarmInfoResourceDetailFeature,
    resourceType: 'workflow',
  },
  eqipPracticeChoiceAgroforestry: {
    Component: RDEqipPracticeChoiceAgroforestryResourceDetailFeature,
    resourceType: 'workflow',
  },
  eqipPracticeChoiceEngineeringLandErosionStructures: {
    Component: RDEqipPracticeChoiceEngineeringLandErosionStructuresResourceDetailFeature,
    resourceType: 'workflow',
  },
  eqipPracticeChoiceEngineeringWaterControlStructures: {
    Component: RDEqipPracticeChoiceEngineeringWaterControlStructuresResourceDetailFeature,
    resourceType: 'workflow',
  },
  eqipPracticeChoiceSoil: {
    Component: RDEqipPracticeChoiceSoilResourceDetailFeature,
    resourceType: 'workflow',
  },
  eqipPracticeChoiceWildlife: {
    Component: RDEqipPracticeChoiceWildlifeResourceDetailFeature,
    resourceType: 'workflow',
  },
  eqipPracticesIrrigationQuantity: {
    Component: RDEqipPracticesIrrigationQuantityResourceDetailFeature,
    resourceType: 'field',
  },
  eqipPracticesNaturalMoisture: {
    Component: RDEqipPracticesNaturalMoistureResourceDetailFeature,
    resourceType: 'field',
  },
  eqipSigningAuthority: EntityMembersFeatureMap as {
    Component: ResourceDetailFeature<any>
    resourceType: 'workflow'
  },
  eqipRecommendedPracticesIntent: {
    Component: RDEqipRecommendedPracticesIntentResourceDetailFeature,
    resourceType: 'workflow',
  },
  practiceSelectionIntentFromPoints: {
    Component: RDEqipRecommendedPracticesIntentResourceDetailFeature,
    resourceType: 'workflow',
  },
  eqipReviewRecommendationResults: {
    Component: RDEqipReviewRecommendationResultsResourceDetailFeature,
    resourceType: 'workflow',
  },
  eqipSelectedPractices: {
    Component: RDEqipFieldsSelectionFeature,
    resourceType: 'field',
  },
  eqipPracticeSelectionConfirmation: {
    Component: RDEqipPracticeSelectionConfirmationFeature,
    resourceType: 'workflow',
  },
  eqipRequiredForms: {
    Component: RDEqipRequiredFormsResourceDetailFeature,
    resourceType: 'workflow',
  },
  eqipFsaForms: {
    Component: RDEqipFsaFormsResourceDetailFeature,
    resourceType: 'workflow',
  },
  eqipOptionalForms: {
    Component: RDEqipOptionalFormsResourceDetailFeature,
    resourceType: 'workflow',
  },
  eqipAutogenRequiredForms: RDEqipAutogenRequiredFormsFeatureMap,

  eqipDocuments: RDEqipDocumentsFeatureMap,

  lolFarmId: {
    Component: RDLolFarmIdResourceDetailFeature,
    resourceType: 'user',
  },
  onFarmDairyFeed: {
    Component: RDOnFarmDairyFeedFeature,
    resourceType: 'field',
  },
  proofOfLandControl: RDEqipProofOfLandControlFeatureMap,
  productAppliedPerPlan: {
    Component: ProductAppliedPerPlan,
    resourceType: 'field',
  },
  usdaAgiThreshold: {
    Component: RDUsdaAgiThresholdResourceDetailFeature,
    resourceType: 'user',
  },
  usdaEarlyAdopter: {
    Component: UsdaEarlyAdopter,
    resourceType: 'user',
  },
  enrolledInUSCottonTrust: {
    Component: EnrolledInUSCottonTrust,
    resourceType: 'user',
  },
  preferredRankPractice: {
    Component: PreferredRankPracticeFeature,
    resourceType: 'field',
  },
  usdaFederalProgramEnrollment: {
    Component: RDUsdaFederalProgramEnrollmentResourceDetailFeature,
    resourceType: 'field',
  },
  // @ts-ignore this accepts additional props b/c it's used in another place as well
  usdaGrowerDetails: UsdaGrowerDetailsFeatureMap,
  usdaGrowerType: {
    Component: RDUsdaGrowerTypeResourceDetailFeature,
    resourceType: 'user',
  },
  usdaGrowerTypeYearly: {
    Component: RDUsdaGrowerTypeYearlyResourceDetailFeature,
    resourceType: 'user',
  },
  usdaUnderservedSmall: {
    Component: RDUsdaUnderservedSmallResourceDetailFeature,
    resourceType: 'user',
  },
  conservationPrograms: {
    Component: ConservationProgramsListTaskDefinition,
    resourceType: 'workflow',
  },
  governmentFunded: {
    Component: GovernmentFundedTaskDefinition,
    resourceType: 'workflow',
  },

  ...LegacyTraitTableFeatures,
}

export const resourceDetailFeatureByTraitId = (traitId?: TraitId) => {
  return traitId ? ResourceDetailFeatureMap[traitId] : undefined
}
